import React, { useState } from "react";
import { observer } from "mobx-react";
import { useFormik } from "formik";
import { Button, Input, Field } from "@chakra-ui/react";
import { t } from "i18next";
import {
  CraWayFormData,
  CreateOrUpdateCraWaySchema,
} from "./CreateOrUpdateCraWaySchema";

import { useStores } from "../../../shared/stores/RootStore";
import { toast } from "../../../shared/views/CustomToaster";
import { CustomDialog } from "../../../shared/views/CustomDialog";
import { CreateCraWay, CraWay, UpdateCraWay } from "../../models/CraWay";

const initialValues: CraWayFormData = {
  name: "",
};

const CreateOrUpdateCraWayModalBase: React.FC<{
  onClose: () => void;
  craWay?: CraWay;
}> = ({ craWay, onClose }) => {
  const { craWayStore, surveyStore } = useStores();
  const [submitting, setSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: craWay || initialValues,

    onSubmit: async (values: CraWayFormData) => {
      setSubmitting(true);
      if (craWay) {
        await updateRoute(values);
      } else {
        await createRoute(values);
      }
      setSubmitting(false);

      formik.setValues(formik.initialValues);
    },
    validationSchema: CreateOrUpdateCraWaySchema,
  });

  const createRoute = async (values: CraWayFormData) => {
    const createRequest: CreateCraWay = {
      name: values.name,
      surveyId: surveyStore.selectedId!,
    };
    await craWayStore.createCraWay(createRequest).then(() => {
      toast("success", t("home:survey.routeCreated"));
      onClose();
    });
  };

  const updateRoute = async (values: CraWayFormData) => {
    const updateRequest: UpdateCraWay = {
      name: values.name,
      description: craWay!.description || "",
      plannedTrajectory: craWay!.plannedTrajectory,
      plannedDistance: craWay!.plannedDistance, // NEED TO ADD DISTANCE
    };
    await craWayStore
      .updateCraWay(craWay!.id, updateRequest)
      .then(() => {
        toast("success", "Route saved");
        onClose();
      })
      .catch((e) => {
        toast("success", "Unable to save route");
        console.warn(e);
      });
  };

  return (
    <CustomDialog
      header={t(
        craWay ? "shared:route.update.title" : "shared:route.create.title",
      )}
      footer={
        <Button
          loading={submitting}
          colorPalette="primary"
          type="submit"
          form="createOrUpdateCraWay"
          disabled={!formik.isValid || !formik.dirty}
        >
          {`${t("shared:survey.submit.done")}`}
        </Button>
      }
      onClose={onClose}
    >
      <form id="createOrUpdateCraWay" onSubmit={formik.handleSubmit}>
        <Field.Root invalid={!!formik.errors.name}>
          <Field.Label>{t("shared:route.fields.name.title")}</Field.Label>
          <Input
            required
            placeholder={`${t("shared:route.fields.name.placeholder")}`}
            name="name"
            onChange={formik.handleChange}
            value={formik.values.name}
          />
          <Field.ErrorText>{formik.errors.name}</Field.ErrorText>
        </Field.Root>
      </form>
    </CustomDialog>
  );
};

export const CreateOrUpdateCraWayModal = observer(
  CreateOrUpdateCraWayModalBase,
);
